import { useState, useEffect } from 'react';
import { RouteConfig, routeConfig } from './RouteConfig';
import { UserRole } from '@/types/database';
import { useIsApprover } from '@/hooks/approvals/useIsApprover';
import { useRemoteRoleAccess } from '@/hooks/useRemoteRoleAccess';

export const useRouteFiltering = (userRole: UserRole | undefined | null) => {
  const { data: remoteAccessConfig, isLoading: loadingAccess } = useRemoteRoleAccess();
  const [filteredRoutes, setFilteredRoutes] = useState<RouteConfig[]>(
    routeConfig.filter(route => route.path === '/dashboard')
  );

  // Check if user is an approver for dynamic menu visibility
  const { data: approverData } = useIsApprover();

  // Filter routes based on user role and access configuration
  useEffect(() => {
    if (!userRole) {
      console.log('No user role provided, showing only dashboard');
      setFilteredRoutes(routeConfig.filter(route => route.path === '/dashboard'));
      return;
    }

    // Default to dashboard while loading or if no role info
    if (loadingAccess) return;

    console.log('Current user role:', userRole);
    let newFilteredRoutes: RouteConfig[] = [];

    // Use remote access config if available, otherwise fallback to RouteConfig hardcoded roles
    const allowedPaths = remoteAccessConfig ? remoteAccessConfig[userRole] : null;

    if (allowedPaths) {
      console.log('Allowed paths for role', userRole, ':', allowedPaths);

      newFilteredRoutes = routeConfig.filter(route => {
        // Skip routes with dynamic visibility - they'll be handled separately
        if (route.dynamicVisibility) return false;

        // Dashboard is ALWAYS allowed
        if (route.path === '/dashboard') return true;

        // Routes flagged as always visible are shown to every role
        if (route.alwaysVisible) return true;

        // Convert route path to ID for matching with configuration
        const routeId = route.path === '/dashboard' ? 'dashboard' : route.path?.substring(1);
        return allowedPaths.includes(routeId || '');
      });

      console.log('Filtered routes from remote access config:', newFilteredRoutes.map(r => r.path));
    } else {
      // Fallback to default role configuration in routes array
      console.log('Using fallback role configuration from routes array');
      newFilteredRoutes = routeConfig.filter(route => {
        if (route.dynamicVisibility) return false;
        if (route.path === '/dashboard') return true;
        if (route.alwaysVisible) return true;
        return route.roles?.includes(userRole);
      });
      console.log('Filtered routes from roles array:', newFilteredRoutes.map(r => r.path));
    }

    // Add dynamic visibility routes based on special conditions
    const dynamicRoutes = routeConfig.filter(route => {
      if (route.dynamicVisibility === 'approver') {
        return approverData?.isApprover === true;
      }
      return false;
    });

    if (dynamicRoutes.length > 0) {
      console.log('Adding dynamic routes:', dynamicRoutes.map(r => r.name));
      const dashboardIndex = newFilteredRoutes.findIndex(r => r.path === '/dashboard');
      if (dashboardIndex !== -1) {
        newFilteredRoutes.splice(dashboardIndex + 1, 0, ...dynamicRoutes);
      } else {
        newFilteredRoutes = [...dynamicRoutes, ...newFilteredRoutes];
      }
    }

    console.log('Final filtered routes for', userRole, ':', newFilteredRoutes.map(r => r.name));
    setFilteredRoutes(newFilteredRoutes);
  }, [userRole, remoteAccessConfig, loadingAccess, approverData]);

  return { filteredRoutes };
};
